import { Show, createEffect, createSignal, onCleanup } from "solid-js";
import { styles } from "../styles";

export type QrDisplayItem = {
  label: string;
  name: string;
  id: string;
  qr: string;
  missingLabel?: string;
};

export function QrCanvas(props: { data: string; label: string; size?: string }) {
  let canvas!: HTMLCanvasElement;

  createEffect(() => {
    const binary = props.data.trim();
    const matrixSize = Math.sqrt(binary.length);
    const context = canvas.getContext("2d");
    if (!context || !binary || !Number.isInteger(matrixSize) || /[^01]/.test(binary)) return;
    const quietZone = 4;
    const cellSize = 5;
    const canvasSize = (matrixSize + quietZone * 2) * cellSize;
    canvas.width = canvasSize;
    canvas.height = canvasSize;
    context.imageSmoothingEnabled = false;
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvasSize, canvasSize);
    context.fillStyle = "#000";
    for (let y = 0; y < matrixSize; y++) {
      for (let x = 0; x < matrixSize; x++) {
        if (binary[y * matrixSize + x] === "1") {
          context.fillRect((x + quietZone) * cellSize, (y + quietZone) * cellSize, cellSize, cellSize);
        }
      }
    }
  });

  return (
    <span class={styles.qrFrame} style={props.size ? { width: props.size } : undefined}>
      <canvas ref={canvas} class={styles.qrCanvas} role="img" aria-label={props.label} />
    </span>
  );
}

export function QrMissing(props: { label?: string }) {
  return <div class={`${styles.qrFrame} ${styles.qrMissing}`} role="status">{props.label ?? "QR未登録"}</div>;
}

export function QrCarousel(props: { items: QrDisplayItem[]; class?: string; cycleIntervalMs?: number }) {
  const [index, setIndex] = createSignal(0);
  const currentItem = () => props.items[Math.min(index(), Math.max(0, props.items.length - 1))];
  const move = (amount: number) => {
    if (props.items.length < 2) return;
    setIndex((current) => (current + amount + props.items.length) % props.items.length);
  };

  createEffect(() => {
    const items = props.items;
    const cycleIntervalMs = Math.min(2_500, Math.max(300, props.cycleIntervalMs ?? 2_500));
    setIndex(0);
    if (items.length < 2) return;
    const interval = window.setInterval(() => {
      setIndex((current) => (current + 1) % items.length);
    }, cycleIntervalMs);
    onCleanup(() => window.clearInterval(interval));
  });

  return (
    <div
      class={`${styles.previewQrList} ${styles.qrCarousel}${props.class ? ` ${props.class}` : ""}`}
      aria-label={props.items.length > 1 ? "QRをコマ送り表示" : undefined}
    >
      <Show when={props.items.length} fallback={<p class={styles.qrEmpty}>表示対象のコーデが選択されていません。</p>}>
        <Show when={currentItem()}>
          {(item) => (
            <section class={styles.previewQrItem}>
              <Show when={item().qr} fallback={<QrMissing label={item().missingLabel} />}>
                <QrCanvas data={item().qr} label={`${item().label} ${item().name} QR`} />
              </Show>
              <span>{item().label}</span>
            </section>
          )}
        </Show>
        <Show when={props.items.length > 1}>
          <div class={styles.qrCarouselControls}>
            <button type="button" aria-label="前のQRを表示" title="前のQR" onClick={() => move(-1)}>◀</button>
            <span>{index() + 1} / {props.items.length}</span>
            <button type="button" aria-label="次のQRを表示" title="次のQR" onClick={() => move(1)}>▶</button>
          </div>
        </Show>
      </Show>
    </div>
  );
}

export function QrList(props: { items: QrDisplayItem[]; cycleIntervalMs?: number }) {
  return <QrCarousel items={props.items} cycleIntervalMs={props.cycleIntervalMs} />;
}
